import { createAsyncThunk } from "@reduxjs/toolkit";
import DineInApiService, {
  toFriendlyDineInError,
} from "../apiServices/DineInApiService";
import { fetchProductsByIds } from "../helpers/ProductLookupHelper";
import { ProductDetailsModal } from "../../interfaces/ProductObject";
import {
  convertRawDineInSession,
  convertRawDineInSessionList,
} from "../converters/backendToUi/dineInConvertor";
import { RawDineInSession } from "../../interfaces/DineInObject";

// ─── Dine In — business/kitchen board ─────────────────────────────────────
// The other side of DineInThunk.ts. Where those thunks act as ONE diner at ONE
// table (and carry raw error codes through so the slice can branch on 10006 as
// a state rather than a failure), these read the whole floor for a business
// and have no session-code exchange to model — so errors are converted to
// display copy here and the slice just shows them.

/**
 * Loads every live dine-in session for a business. Fired when the Dine-in tab
 * mounts, and by any explicit retry.
 *
 * Rejects with FRIENDLY copy rather than a raw code: none of the dining error
 * codes represent a recoverable state for the board the way
 * INVALID_OR_MISSING_SESSION_CODE does for a diner — there's nothing to
 * branch on, so there's no reason to leak a code the UI would have to
 * translate anyway.
 */
export const fetchDineInBoardSessions = createAsyncThunk(
  "dineInBoard/fetchSessions",
  async ({ businessId }: { businessId: string }, { rejectWithValue }) => {
    try {
      const raw = await DineInApiService.getSessionsByBusiness(businessId);
      return { businessId, sessions: convertRawDineInSessionList(raw) };
    } catch (error) {
      return rejectWithValue(toFriendlyDineInError(error));
    }
  }
);

/**
 * Same read as fetchDineInBoardSessions, but SILENT — the slice's reducers for
 * this one deliberately touch neither loadingMap nor errorMap.
 *
 * This matters on the device this screen actually runs on: a tablet propped on
 * a counter, refreshed whenever staff glance back at it. Routing background
 * refreshes through the loading path would blank a floor full of live tables
 * into skeletons every time, and routing failures through the error path would
 * replace a still-perfectly-usable board with a retry screen because one
 * refetch lost the network. Stale-but-visible beats correct-but-gone here;
 * the next refresh fixes it either way.
 *
 * Mirrors DineInThunk.refreshDineInSession, which does the same for a diner.
 */
export const refreshDineInBoardSessions = createAsyncThunk(
  "dineInBoard/refreshSessions",
  async ({ businessId }: { businessId: string }, { rejectWithValue }) => {
    try {
      const raw = await DineInApiService.getSessionsByBusiness(businessId);
      return { businessId, sessions: convertRawDineInSessionList(raw) };
    } catch (error) {
      console.warn(
        "refreshDineInBoardSessions failed (will retry on next focus/event):",
        error
      );
      return rejectWithValue(null);
    }
  }
);

/**
 * Resolves name/price for every productId referenced across the board's
 * sessions. Sessions only carry productIds, and prices are needed for the
 * per-table totals and the floor-wide running total.
 *
 * Deliberately its own thunk rather than reusing DineInThunk's: that one
 * writes into the diner slice, and a member of staff viewing the board is not
 * seated at a table. The two slices stay completely disjoint — only this
 * shared fetch helper is common to both.
 */
export const fetchDineInBoardProducts = createAsyncThunk(
  "dineInBoard/fetchProducts",
  async (productIds: string[], { rejectWithValue }) => {
    try {
      const products: ProductDetailsModal[] = await fetchProductsByIds(productIds);
      return products;
    } catch (e) {
      return rejectWithValue("Failed to load menu item details");
    }
  }
);

/**
 * The kitchen accepting or rejecting items.
 *
 * Takes a LIST of ids because the ticket groups identical lines: "Paneer
 * Sandwich ×2" can be two separate rows in round_items, and the endpoint
 * decides one row at a time. Accepting the line has to mean accepting both.
 *
 * Sent SEQUENTIALLY, not in parallel. Every call returns the whole session, so
 * firing them together would produce N responses racing to be applied, each
 * built from a different moment of server state — and the last one to arrive,
 * not the last one to execute, would win. In series the final response is
 * unambiguously the newest, and it's the one that gets stored.
 *
 * A mid-list failure is left as-is deliberately: some items are decided, some
 * aren't, and rather than guess or attempt a rollback (there's no compensating
 * endpoint), the caller refetches so the board shows what the server actually
 * did.
 */
export const decideDineInItems = createAsyncThunk(
  "dineInBoard/decideItems",
  async (
    {
      roundItemIds,
      status,
      note,
    }: { roundItemIds: string[]; status: string; note?: string },
    { rejectWithValue }
  ) => {
    if (roundItemIds.length === 0) {
      return rejectWithValue("That item can't be updated yet.");
    }
    try {
      let latest: RawDineInSession | null = null;
      for (const roundItemId of roundItemIds) {
        latest = await DineInApiService.decideItem({ roundItemId, status, note });
      }
      return convertRawDineInSession(latest as RawDineInSession);
    } catch (error) {
      return rejectWithValue(toFriendlyDineInError(error));
    }
  }
);

/**
 * The owner/collaborator deciding an entire round in one call.
 *
 * A single request, unlike decideDineInItems — which is the reason this exists
 * rather than looping the item endpoint. Whether the server cascades the
 * verdict down to the round's items is not assumed here either way: the
 * response is the whole session, so the UI simply renders whatever actually
 * changed rather than predicting it.
 */
export const decideDineInRound = createAsyncThunk(
  "dineInBoard/decideRound",
  async (
    { roundId, status, note }: { roundId: string; status: string; note?: string },
    { rejectWithValue }
  ) => {
    try {
      const raw = await DineInApiService.decideRound({ roundId, status, note });
      return convertRawDineInSession(raw);
    } catch (error) {
      return rejectWithValue(toFriendlyDineInError(error));
    }
  }
);

const DineInBoardThunk = {
  fetchDineInBoardSessions,
  refreshDineInBoardSessions,
  fetchDineInBoardProducts,
  decideDineInItems,
  decideDineInRound,
};

export default DineInBoardThunk;
